import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  INVITE_CODE_LENGTH,
  MAX_POOL_NAME_LENGTH,
  normalizeInviteCode,
  POOL_LEADERBOARD_PAGE_SIZE,
  type InProgressPlayer,
  type LobbyListItem,
  type LobbyVisibility,
  type MyLobbyItem,
  type PoolLeaderboardResponse,
  type PoolStanding,
  type SkillPool,
} from "@fortress/shared";
import {
  cancelMatch,
  closeLobby,
  createLobby,
  joinLobbyByCode,
  listLobbies,
  listMyLobbies,
  listMyMatches,
} from "./auth/lobbies";
import { createPool, fetchPoolLeaderboard, listPools } from "./auth/pools";
import { getActiveGameTokens } from "./net/activeGame";
import { getAppState } from "./appState";
import { AccountPanel } from "./AccountPanel";
import { FortressMark } from "./FortressLogo";
import { MatchHistory, RecentMatches } from "./MatchHistory";
import { ResourceRow } from "./resources";

/** One in-progress match of this account, as shown on the hub. Every row is
 * entered the same way — by its `gameId` (`/match/:gameId` revives a dormant
 * match on the way in); `live` only tunes the label. The guest-mode fallback
 * rows carry the locally stored reconnection token instead (no account to
 * query matches by) and no match info. */
interface HubMatch {
  gameId: string;
  /** A live room is hosting it right now (label hint only). */
  live: boolean;
  /** This account's seat is connected from another device / tab. */
  openElsewhere: boolean;
  roundNumber: number | null;
  phase: string | null;
  players: InProgressPlayer[];
  /** This account holds the current turn. */
  yourTurn: boolean;
  /** Guest (AUTH_DISABLED) fallback only — reconnect via stored token. */
  reconnectionToken?: string;
}

interface LobbyHubProps {
  /** A signed-in account is behind this client — false under AUTH_DISABLED,
   * where there is no account for the Account tab to show. */
  authenticated?: boolean;
  onEnterMatch: (gameId: string, opts?: { spectate?: boolean }) => void;
  /** Guest fallback: re-enter a live game with the stored reconnection token. */
  onReconnect: (game: { gameId: string; reconnectionToken: string }) => void;
  /** The last reconnect attempt failed — the game is gone; shown once. */
  reconnectFailed?: boolean;
  /** The lobby we were just in was closed by its creator; shown once. */
  lobbyClosed?: boolean;
  /** Our account joined the same game from another device, which took the
   * seat and disconnected this one; shown once. */
  sessionSuperseded?: boolean;
  /** False until the account has finished (or skipped) the tutorial: the
   * home tab then leads with the Play tutorial card. */
  tutorialComplete?: boolean;
}

function statusLabel(status: LobbyListItem["status"]): string {
  switch (status) {
    case "waiting":
      return "Waiting";
    case "full":
      return "Full";
    case "in_progress":
      return "In progress";
  }
}

/** A player's standing on one leaderboard row — the same rating/record/streak
 * chips the lobby roster uses, so ranked numbers read identically everywhere. */
function StandingRating({ row }: { row: PoolStanding }) {
  return (
    <span className="lobby-rating">
      <span className="lobby-rating-mmr">{Math.round(row.rating)}</span>
      {row.matches === 0 ? (
        <span className="lobby-rating-record is-unrated">No matches</span>
      ) : (
        <span className="lobby-rating-record">
          {row.wins}W–{row.losses}L
        </span>
      )}
      {row.streak !== 0 && (
        <span
          className={`lobby-rating-streak ${row.streak > 0 ? "is-win" : "is-loss"}`}
          title={
            row.streak > 0
              ? `${row.streak} win${row.streak === 1 ? "" : "s"} in a row`
              : `${-row.streak} loss${row.streak === -1 ? "" : "es"} in a row`
          }
        >
          {row.streak > 0 ? `W${row.streak}` : `L${-row.streak}`}
        </span>
      )}
    </span>
  );
}

/**
 * One expanded pool's leaderboard — every rated player, best first, fetched a
 * page at a time from the server (which proxies Inhouse). A pool's creator is
 * seeded as a rated player, so a brand-new pool shows them at the starting
 * rating with "No matches" rather than an empty board.
 */
function PoolLeaderboard({ poolId }: { poolId: string }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PoolLeaderboardResponse | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stale = false;
    setBusy(true);
    setError(null);
    fetchPoolLeaderboard(poolId, page)
      .then((res) => {
        if (!stale) setData(res);
      })
      .catch(() => {
        if (!stale) setError("Couldn't load this pool's standings.");
      })
      .finally(() => {
        if (!stale) setBusy(false);
      });
    return () => {
      stale = true;
    };
  }, [poolId, page]);

  const standings = data?.standings ?? [];
  /** Rank of the first row on this page (ranks continue across pages). */
  const rankBase = ((data?.page ?? page) - 1) * POOL_LEADERBOARD_PAGE_SIZE;

  return (
    <div className="lobby-hub-pool-board">
      {error && <p className="auth-error">{error}</p>}
      {busy && standings.length === 0 && !error && (
        <p className="lobby-hub-empty">Loading standings…</p>
      )}
      {!busy && !error && standings.length === 0 && (
        <p className="lobby-hub-empty">
          No rated players yet. Standings appear once a ranked match reports to
          this pool.
        </p>
      )}
      {standings.length > 0 && (
        <ol className="lobby-hub-pool-standings">
          {standings.map((row, i) => (
            <li
              key={`${row.name}-${rankBase + i}`}
              className={`lobby-hub-pool-standing${row.you ? " is-you" : ""}`}
            >
              <span className="lobby-hub-pool-rank">#{rankBase + i + 1}</span>
              <span className="lobby-hub-pool-player">
                {row.name}
                {row.you && <span className="lobby-hub-pool-you">You</span>}
              </span>
              <StandingRating row={row} />
            </li>
          ))}
        </ol>
      )}
      {(page > 1 || data?.hasMore) && (
        <div className="lobby-hub-pager">
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="lobby-hub-pager-label">Page {page}</span>
          <button
            type="button"
            className="lobby-hub-watch"
            disabled={busy || !data?.hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Skill rating pools — independent rating populations a lobby host can rate a
 * match in. Anyone signed in can add one. The server scopes this list to the
 * signed-in player (pools they made, plus any they've been rated in), so it
 * doesn't grow with every other player's pools.
 */
function PoolsPanel() {
  const [pools, setPools] = useState<SkillPool[]>([]);
  /** False when the server has no rating API key configured. */
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Pool whose standings are open beneath its row, or null. */
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const body = await listPools();
      setPools(body.pools);
      setEnabled(body.enabled);
      setError(null);
    } catch {
      setError("Couldn't load skill rating pools.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const pool = await createPool(trimmed);
      setPools((prev) =>
        [...prev, pool].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setName("");
    } catch {
      setError("Couldn't create that pool. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="lobby-hub-panel lobby-hub-list">
      <div className="lobby-hub-list-head">
        <h2>Skill rating pools</h2>
      </div>
      <p className="lobby-hub-empty">
        A pool is its own rating population. The host of a lobby can pick one to
        make that match ranked. You&apos;ll see the pools you created, plus any
        you&apos;ve played a ranked match in.
      </p>

      {!enabled && !loading && (
        <p className="auth-error">
          Ranked play isn&apos;t configured on this server.
        </p>
      )}

      {enabled && (
        <form className="lobby-hub-form lobby-hub-pool-form" onSubmit={onCreate}>
          <label className="auth-label" htmlFor="pool-name">
            New pool name
          </label>
          <input
            id="pool-name"
            className="auth-input"
            type="text"
            placeholder="Ranked 4-player"
            maxLength={MAX_POOL_NAME_LENGTH}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <button
            className="auth-submit"
            type="submit"
            disabled={busy || !name.trim()}
          >
            {busy ? "Creating…" : "Create pool"}
          </button>
        </form>
      )}
      {error && <p className="auth-error">{error}</p>}

      {enabled && !loading && pools.length === 0 && (
        <p className="lobby-hub-empty">
          You&apos;re not in any pools yet. Create one above.
        </p>
      )}
      <ul className="lobby-hub-rows">
        {pools.map((pool) => {
          const expanded = expandedId === pool.poolId;
          return (
            <li key={pool.poolId} className="lobby-hub-row lobby-hub-pool-row">
              <button
                type="button"
                className="lobby-hub-history-toggle"
                aria-expanded={expanded}
                onClick={() => setExpandedId(expanded ? null : pool.poolId)}
              >
                <span className="lobby-hub-host">{pool.name}</span>
                <span className="lobby-hub-pool-hint">
                  {expanded ? "Hide standings" : "View standings"}
                </span>
              </button>
              {expanded && <PoolLeaderboard poolId={pool.poolId} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The hub's five sections, each at its own path so a refresh (or the
 * `returnTo` after login) lands back on the same tab. Switching REPLACES the
 * history entry rather than pushing one: the tabs are peers, so Back leaves
 * the hub (the Android back button closes the app) instead of walking back
 * through every tab visited. */
export type HubTab = "home" | "lobbies" | "matches" | "pools" | "account";

const TAB_PATHS: Record<HubTab, string> = {
  home: "/",
  lobbies: "/lobbies",
  matches: "/matches",
  pools: "/pools",
  account: "/account",
};

/** Which tab a hub path addresses; anything unrecognised is home. */
function hubTabFromPath(pathname: string): HubTab {
  for (const tab of Object.keys(TAB_PATHS) as HubTab[]) {
    const path = TAB_PATHS[tab];
    if (
      tab !== "home" &&
      (pathname === path || pathname.startsWith(`${path}/`))
    ) {
      return tab;
    }
  }
  return "home";
}

const TAB_SUBTITLES: Record<HubTab, string> = {
  home: "Your matches at a glance.",
  lobbies: "Create a lobby, browse public games, or enter a code.",
  matches: "Every match you've played.",
  pools: "The skill rating pools you're part of.",
  account: "Your profile, notifications, and sign-out.",
};

/** Nav entries — a tab row under the header on wide screens, a fixed bottom
 * bar on small ones (styles.css). Icons are inline stroke SVGs so they take
 * the item's colour. */
const NAV_ITEMS: { tab: HubTab; label: string; icon: ReactNode }[] = [
  {
    tab: "home",
    label: "Home",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 11.5 12 4l9 7.5M5.5 10v10h13V10" />
      </svg>
    ),
  },
  {
    tab: "lobbies",
    label: "Lobbies",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
        <circle cx="17" cy="9" r="2.6" />
        <path d="M15.5 14.5A4.5 4.5 0 0 1 21 19" />
      </svg>
    ),
  },
  {
    tab: "matches",
    label: "Matches",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </svg>
    ),
  },
  {
    tab: "pools",
    label: "Pools",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 4h8v5a4 4 0 0 1-8 0V4zM8 6H5a3 3 0 0 0 3 3M16 6h3a3 3 0 0 1-3 3M12 13v4M9 20h6M10.5 17h3" />
      </svg>
    ),
  },
  {
    tab: "account",
    label: "Account",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8.5" r="3.5" />
        <path d="M5 20a7 7 0 0 1 14 0" />
      </svg>
    ),
  },
];

/** The Create-lobby settings — the Lobbies tab's panel and the home tab's
 * New match dialog both render this one form. A successful create hands the
 * new lobby to `onEnterMatch`, which leaves the hub. */
function CreateLobbyForm({
  onEnterMatch,
}: {
  onEnterMatch: (gameId: string) => void;
}) {
  const [visibility, setVisibility] = useState<LobbyVisibility>("public");
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const id = useId();

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await createLobby({ visibility, maxPlayers });
      onEnterMatch(created.gameId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "create_failed";
      setCreateError(
        msg === "too_many_lobbies"
          ? "You already have too many lobbies waiting for players. Close or start one before creating another."
          : msg,
      );
      setCreateBusy(false);
    }
  }

  return (
    <form className="lobby-hub-form" onSubmit={onCreate}>
      <label className="auth-label" htmlFor={`${id}-max`}>
        Max players
      </label>
      <select
        id={`${id}-max`}
        className="auth-input"
        value={maxPlayers}
        onChange={(e) => setMaxPlayers(Number(e.target.value))}
        disabled={createBusy}
      >
        {Array.from({ length: 7 }, (_, i) => i + 2).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>

      <fieldset className="lobby-hub-visibility">
        <legend className="auth-label">Visibility</legend>
        <label className="lobby-hub-radio">
          <input
            type="radio"
            name={`${id}-visibility`}
            checked={visibility === "public"}
            onChange={() => setVisibility("public")}
            disabled={createBusy}
          />
          Public
        </label>
        <label className="lobby-hub-radio">
          <input
            type="radio"
            name={`${id}-visibility`}
            checked={visibility === "private"}
            onChange={() => setVisibility("private")}
            disabled={createBusy}
          />
          Private
        </label>
      </fieldset>

      <p className="lobby-hub-create-hint">
        Ready players keep their seat even after closing the game; the match
        starts once everyone is ready.
      </p>

      <button className="auth-submit" type="submit" disabled={createBusy}>
        {createBusy ? "Creating…" : "Create"}
      </button>
      {createError && <p className="auth-error">{createError}</p>}
      {/* A private solo match that is already under way and never saved —
          for looking at the board and panels without a real game. */}
      <a className="auth-link" href="/sandbox">
        Just want to look around? Open a sandbox
      </a>
    </form>
  );
}

/** The Create-lobby form in an overlay, opened by the home tab's New match
 * button: a centred modal on wide screens, a full-page sheet sliding up on
 * small ones (styles.css). Escape and the backdrop close it. Portalled to
 * <body> so it stacks above the floating rulebook button, which sits
 * outside the hub's own stacking context. */
function NewMatchDialog({
  onClose,
  onEnterMatch,
}: {
  onClose: () => void;
  onEnterMatch: (gameId: string) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="hub-modal"
      role="dialog"
      aria-modal="true"
      aria-label="New match"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="hub-modal-panel">
        <div className="hub-modal-head">
          <h2>New match</h2>
          <button
            type="button"
            className="hub-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="hub-modal-body">
          <CreateLobbyForm onEnterMatch={onEnterMatch} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function LobbyHub({
  authenticated = false,
  onEnterMatch,
  onReconnect,
  reconnectFailed = false,
  lobbyClosed = false,
  sessionSuperseded = false,
  tutorialComplete = true,
}: LobbyHubProps) {
  const [tab, setTab] = useState<HubTab>(() =>
    hubTabFromPath(window.location.pathname),
  );
  const selectTab = useCallback((next: HubTab) => {
    setTab(next);
    if (window.location.pathname !== TAB_PATHS[next]) {
      window.history.replaceState(null, "", TAB_PATHS[next]);
    }
  }, []);
  // Back/forward landing on a hub path (e.g. returning from a match that was
  // opened off the Lobbies tab) re-selects that tab.
  useEffect(() => {
    const onPopState = () => setTab(hubTabFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const [lobbies, setLobbies] = useState<LobbyListItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [listBusy, setListBusy] = useState(false);

  /** This account's in-progress matches. The games DB is the source of truth
   * (`GET /games/mine`); entering any row is just `/match/:gameId` — the
   * room endpoint returns the live room or revives one from the save, and
   * the room's returning-participant gate seats the account back (no local
   * token needed, works cross-device). With AUTH_DISABLED there is no
   * account to query by, so the locally recorded matches are offered as
   * token-based reconnects instead (a dead one fails on click). */
  const [myMatches, setMyMatches] = useState<HubMatch[]>([]);
  const refreshMyMatches = useCallback(async () => {
    try {
      const { authDisabled } = await getAppState();
      if (authDisabled) {
        const tokens = await getActiveGameTokens();
        setMyMatches(
          Object.entries(tokens).map(([gameId, reconnectionToken]) => ({
            gameId,
            live: true,
            openElsewhere: false,
            roundNumber: null,
            phase: null,
            players: [],
            yourTurn: false,
            reconnectionToken,
          })),
        );
        return;
      }
      const matches = await listMyMatches();
      setMyMatches(
        matches.map((m) => ({
          gameId: m.gameId,
          live: m.live,
          openElsewhere: m.openElsewhere,
          roundNumber: m.roundNumber,
          phase: m.phase,
          players: m.players,
          yourTurn: m.yourTurn,
        })),
      );
    } catch {
      // Transient lookup failure — keep whatever was last shown.
    }
  }, []);

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);

  /** This account's open (waiting) lobbies — the create-cap management list.
   * Empty for guests (no account to key on). */
  const [myLobbies, setMyLobbies] = useState<MyLobbyItem[]>([]);
  const [closingLobbyId, setClosingLobbyId] = useState<string | null>(null);
  const refreshMyLobbies = useCallback(async () => {
    try {
      const { authDisabled } = await getAppState();
      if (authDisabled) return;
      setMyLobbies(await listMyLobbies());
    } catch {
      // Transient lookup failure — keep whatever was last shown.
    }
  }, []);

  const onCloseLobby = useCallback(
    async (gameId: string) => {
      setClosingLobbyId(gameId);
      setMatchError(null);
      try {
        await closeLobby(gameId);
        setMyLobbies((rows) => rows.filter((row) => row.gameId !== gameId));
        void refreshMyMatches();
      } catch {
        setMatchError("Couldn't close that lobby. Try again.");
      } finally {
        setClosingLobbyId(null);
      }
    },
    [refreshMyMatches],
  );

  const onCancelMatch = useCallback(async (gameId: string) => {
    setCancellingId(gameId);
    setMatchError(null);
    try {
      await cancelMatch(gameId);
      setMyMatches((matches) =>
        matches.filter((match) => match.gameId !== gameId),
      );
    } catch {
      setMatchError("Couldn't cancel that game. Try again.");
    } finally {
      setCancellingId(null);
    }
  }, []);

  const [newMatchOpen, setNewMatchOpen] = useState(false);
  const closeNewMatch = useCallback(() => setNewMatchOpen(false), []);

  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setListBusy(true);
    setListError(null);
    try {
      setLobbies(await listLobbies());
    } catch (err) {
      setListError(err instanceof Error ? err.message : "list_failed");
    } finally {
      setListBusy(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void refreshMyMatches();
    void refreshMyLobbies();
    const id = window.setInterval(() => {
      void refresh();
      void refreshMyMatches();
      void refreshMyLobbies();
    }, 5000);
    return () => window.clearInterval(id);
  }, [refresh, refreshMyMatches, refreshMyLobbies]);

  async function joinByCode(spectate: boolean) {
    const normalized = normalizeInviteCode(code);
    if (normalized.length !== INVITE_CODE_LENGTH) {
      setCodeError("Enter a 6-character code.");
      return;
    }
    setCodeBusy(true);
    setCodeError(null);
    try {
      const { gameId } = await joinLobbyByCode({
        code: normalized,
        ...(spectate ? { spectate: true } : {}),
      });
      onEnterMatch(gameId, spectate ? { spectate: true } : undefined);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "join_failed";
      setCodeError(
        msg === "not_found"
          ? "No lobby with that code."
          : msg === "not_joinable"
            ? "That lobby is full or already started."
            : "Couldn't join. Try again.",
      );
      setCodeBusy(false);
    }
  }

  async function onJoinCode(e: FormEvent) {
    e.preventDefault();
    await joinByCode(false);
  }

  async function onJoinListed(gameId: string) {
    setListError(null);
    onEnterMatch(gameId);
  }

  /** Changes when the in-progress set changes — a match leaving it just
   * ended, which is the home tab's cue to re-fetch recent results. */
  const activeMatchesKey = myMatches
    .map((m) => m.gameId)
    .sort()
    .join("|");

  const activeMatchTiles = myMatches.map((match) => {
    const live = match.live;
    // A lobby-phase row is a lobby holding this account's seat
    // (readied, then exited) — leaving unready relinquishes the seat
    // and drops out of `games/mine`.
    const inLobby = match.phase === "lobby";
    const title = inLobby
      ? "Waiting in lobby"
      : match.phase === "setup"
        ? "Setting up"
        : match.roundNumber !== null
          ? `Round ${match.roundNumber}`
          : "Game in progress";
    // Open on another device: entering here moves the seat to this one.
    const note = match.openElsewhere
      ? "Open on another device"
      : inLobby
        ? live
          ? "Starts when everyone is ready"
          : "Your seat is saved"
        : live
          ? null
          : "Paused";
    // Paused saves can also be canceled; a cancel in flight locks the tile.
    const paused = !inLobby && !live;
    return (
      <li
        key={match.gameId}
        className={`lobby-hub-row lobby-hub-active${match.yourTurn ? " is-your-turn" : ""}`}
      >
        <div className="lobby-hub-active-top">
          <span className="lobby-hub-active-title">{title}</span>
          {note && <span className="lobby-hub-active-note">{note}</span>}
          {match.yourTurn && (
            <span className="lobby-hub-active-turn">Your turn</span>
          )}
        </div>
        {match.players.length > 0 && (
          <ul className="lobby-hub-active-players">
            {match.players.map((p, i) => (
              <li
                key={`${p.name}-${i}`}
                className={`lobby-hub-active-player${p.you ? " is-you" : ""}${p.turn ? " is-turn" : ""}`}
                aria-current={p.turn ? "true" : undefined}
                title={p.turn ? "Current turn" : undefined}
              >
                <span className="lobby-hub-active-name">
                  {p.name}
                  {p.you && <span className="lobby-hub-active-you">you</span>}
                </span>
                <ResourceRow
                  label="Bank"
                  resources={p.resources}
                  oneTime
                  iconSize={16}
                  highlight
                />
                <ResourceRow
                  label="Production"
                  resources={p.production}
                  iconSize={16}
                />
                <span
                  className="lobby-hub-active-forts"
                  title="Implemented fortresses"
                >
                  {p.winFortresses} win · {p.regularFortresses} regular
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="lobby-hub-match-actions">
          <button
            type="button"
            className="auth-submit lobby-hub-active-play"
            disabled={paused && cancellingId !== null}
            onClick={() =>
              match.reconnectionToken
                ? onReconnect({
                    gameId: match.gameId,
                    reconnectionToken: match.reconnectionToken,
                  })
                : onEnterMatch(match.gameId)
            }
          >
            {inLobby ? "Return to lobby" : "Play"}
          </button>
          {paused && (
            <button
              type="button"
              className="lobby-hub-cancel-match"
              aria-label="Cancel saved game"
              title="Cancel saved game"
              disabled={cancellingId !== null}
              onClick={() => void onCancelMatch(match.gameId)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 6h18M8 6V4h8v2m2 0-1 14H7L6 6m4 4v6m4-6v6" />
              </svg>
            </button>
          )}
        </div>
      </li>
    );
  });

  return (
    <div className="lobby-hub">
      {/* Brand bar: the mark + wordmark, with the section nav beside it on
          wide screens (it becomes a fixed bottom bar ≤640px — hub.css). */}
      <header className="lobby-hub-header">
        <div className="lobby-hub-bar">
          <h1 className="lobby-hub-title">
            <FortressMark className="lobby-hub-mark" />
            Fortress
          </h1>
          <nav className="hub-nav" aria-label="Sections">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.tab}
                type="button"
                className={`hub-nav-item${tab === item.tab ? " is-active" : ""}`}
                aria-current={tab === item.tab ? "page" : undefined}
                onClick={() => selectTab(item.tab)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </header>

      <div className="hub-page-head">
        <div>
          <h2 className="hub-page-title">
            {NAV_ITEMS.find((item) => item.tab === tab)?.label}
          </h2>
          <p className="lobby-hub-sub">{TAB_SUBTITLES[tab]}</p>
        </div>
        {tab === "home" && (
          <button
            type="button"
            className="auth-submit hub-new-match"
            onClick={() => setNewMatchOpen(true)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New match
          </button>
        )}
      </div>

      {tab === "home" && (
        <>
          {!tutorialComplete && (
            <section className="lobby-hub-panel hub-tutorial-card">
              <div>
                <h2>New to Fortress?</h2>
                <p className="lobby-hub-empty">
                  A ten-minute guided match against a bot teaches every basic
                  rule — building, resources, combat, fortresses and winning.
                </p>
              </div>
              {/* A full navigation, like the sandbox link: the page mints the
                  tutorial match and enters it. */}
              <a className="auth-submit hub-tutorial-play" href="/tutorial">
                Play tutorial
              </a>
            </section>
          )}
          {myMatches.length > 0 && (
            <section className="lobby-hub-panel lobby-hub-list">
              <div className="lobby-hub-list-head">
                <h2>In progress</h2>
              </div>
              <ul className="lobby-hub-rows">{activeMatchTiles}</ul>
            </section>
          )}
          {myMatches.length === 0 && (
            <section className="lobby-hub-panel hub-home-empty">
              <h2>No matches in progress</h2>
              <p className="lobby-hub-empty">
                Create a lobby or join one with a code to start playing.
              </p>
              <button
                type="button"
                className="auth-submit"
                onClick={() => selectTab("lobbies")}
              >
                Find a game
              </button>
            </section>
          )}
          {matchError && (
            <p className="auth-error lobby-hub-reconnect-error">{matchError}</p>
          )}
          {reconnectFailed && myMatches.length === 0 && (
            <p className="auth-error lobby-hub-reconnect-error">
              Couldn&apos;t reconnect — that game is no longer available.
            </p>
          )}
          {lobbyClosed && (
            <p className="auth-error lobby-hub-reconnect-error">
              That lobby was closed by its creator.
            </p>
          )}
          {sessionSuperseded && (
            <p className="auth-error lobby-hub-reconnect-error">
              You joined that game on another device, so this one was
              disconnected.
            </p>
          )}
          <RecentMatches
            onEnterMatch={onEnterMatch}
            onSeeAll={() => selectTab("matches")}
            refreshKey={activeMatchesKey}
          />
          {newMatchOpen && (
            <NewMatchDialog
              onClose={closeNewMatch}
              onEnterMatch={onEnterMatch}
            />
          )}
        </>
      )}

      {tab === "lobbies" && (
        <>
          {matchError && (
            <p className="auth-error lobby-hub-reconnect-error">{matchError}</p>
          )}

          {myLobbies.length > 0 && (
            <section className="lobby-hub-panel lobby-hub-list">
              <div className="lobby-hub-list-head">
                <h2>Your open lobbies</h2>
              </div>
              <p className="lobby-hub-empty">
                Lobbies you created that are still waiting for players — they
                survive restarts until you start or close them. You can have
                up to 3 open at once.
              </p>
              <ul className="lobby-hub-rows">
                {myLobbies.map((lobby) => (
                  <li key={lobby.gameId} className="lobby-hub-row">
                    <div className="lobby-hub-row-main">
                      <code className="lobby-hub-mylobby-code">
                        {lobby.inviteCode}
                      </code>
                      <span className="lobby-hub-counts">
                        {lobby.playerCount}/{lobby.maxPlayers}
                      </span>
                      <span className="lobby-hub-host">
                        {lobby.players.length > 0
                          ? lobby.players.join(", ")
                          : "No members yet"}
                        {lobby.visibility === "private" ? " · Private" : ""}
                      </span>
                    </div>
                    <div className="lobby-hub-row-actions">
                      <button
                        type="button"
                        className="lobby-hub-join"
                        disabled={closingLobbyId !== null}
                        onClick={() => onEnterMatch(lobby.gameId)}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className="lobby-hub-close-lobby"
                        title="Close this lobby for everyone."
                        disabled={closingLobbyId !== null}
                        onClick={() => void onCloseLobby(lobby.gameId)}
                      >
                        {closingLobbyId === lobby.gameId ? "Closing…" : "Close"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="lobby-hub-grid">
            <section className="lobby-hub-panel">
              <h2>Create lobby</h2>
              <CreateLobbyForm onEnterMatch={onEnterMatch} />
            </section>

            <section className="lobby-hub-panel">
              <h2>Join with code</h2>
              <form className="lobby-hub-form" onSubmit={onJoinCode}>
                <label className="auth-label" htmlFor="lobby-code">
                  Invite code
                </label>
                <input
                  id="lobby-code"
                  className="auth-input lobby-code-input"
                  type="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="ABC123"
                  maxLength={INVITE_CODE_LENGTH}
                  value={code}
                  onChange={(e) =>
                    setCode(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, INVITE_CODE_LENGTH),
                    )
                  }
                  disabled={codeBusy}
                />
                <button className="auth-submit" type="submit" disabled={codeBusy}>
                  {codeBusy ? "Joining…" : "Join"}
                </button>
                <button
                  type="button"
                  className="lobby-hub-watch-code"
                  disabled={codeBusy}
                  title="Watch this game without playing"
                  onClick={() => void joinByCode(true)}
                >
                  Watch as spectator
                </button>
                {codeError && <p className="auth-error">{codeError}</p>}
              </form>
            </section>
          </div>

          <section className="lobby-hub-panel lobby-hub-list">
            <div className="lobby-hub-list-head">
              <h2>Public lobbies</h2>
              <button
                type="button"
                className="auth-link"
                onClick={() => void refresh()}
                disabled={listBusy}
              >
                Refresh
              </button>
            </div>
            {listError && <p className="auth-error">{listError}</p>}
            {lobbies.length === 0 && !listBusy && (
              <p className="lobby-hub-empty">No public lobbies right now.</p>
            )}
            <ul className="lobby-hub-rows">
              {lobbies.map((lobby) => {
                const joinable = lobby.status === "waiting";
                return (
                  <li key={lobby.gameId} className="lobby-hub-row">
                    <div className="lobby-hub-row-main">
                      <span className={`lobby-hub-status is-${lobby.status}`}>
                        {statusLabel(lobby.status)}
                      </span>
                      <span className="lobby-hub-counts">
                        {lobby.playerCount}/{lobby.maxPlayers}
                      </span>
                      {lobby.hostName && (
                        <span className="lobby-hub-host">{lobby.hostName}</span>
                      )}
                    </div>
                    <div className="lobby-hub-row-actions">
                      <button
                        type="button"
                        className="lobby-hub-join"
                        disabled={!joinable}
                        onClick={() => void onJoinListed(lobby.gameId)}
                      >
                        {joinable ? "Join" : "—"}
                      </button>
                      <button
                        type="button"
                        className="lobby-hub-watch"
                        title="Watch without playing"
                        onClick={() => onEnterMatch(lobby.gameId, { spectate: true })}
                      >
                        Watch
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}

      {tab === "matches" && <MatchHistory onEnterMatch={onEnterMatch} />}
      {tab === "pools" && <PoolsPanel />}
      {tab === "account" && <AccountPanel authenticated={authenticated} />}
    </div>
  );
}
